/**
 * Copy and paste within and between graphics: the clipboard carries JSON with the elements, the asset entries
 * they use and the fields bound only to them. Pasting gives everything fresh ids and validates it.
 */
import { AssetSchema, ElementSchema, FieldSchema, type Asset, type Element, type Field, type Scene } from '@prism-graphics/scene-schema';
import { z } from 'zod';
import { addElements, cloneWithNewIds, ensureAsset, type InsertAt } from './commands.ts';
import { fieldIdFrom } from './ids.ts';
import { findElement, topmost, walk } from './tree.ts';

export const CLIPBOARD_FORMAT = 'prism-graphics/elements';

export interface ClipboardPayload {
  format: typeof CLIPBOARD_FORMAT;
  version: 1;
  elements: Element[];
  assets: Asset[];
  fields: Field[];
}

const PayloadSchema = z.object({
  format: z.literal(CLIPBOARD_FORMAT),
  version: z.literal(1),
  elements: z.array(ElementSchema).min(1),
  assets: z.array(AssetSchema).default([]),
  fields: z.array(FieldSchema).default([]),
});

export function copySelection(scene: Scene, ids: readonly string[]): ClipboardPayload | undefined {
  const roots = topmost(scene, ids)
    .map((id) => findElement(scene, id))
    .filter((e): e is Element => e !== undefined);
  if (roots.length === 0) return undefined;
  const inside = new Set<string>();
  const hashes = new Set<string>();
  walk(roots, (el) => {
    inside.add(el.id);
    if (el.type === 'text') hashes.add(el.style.font);
    if (el.type === 'image' || el.type === 'video') hashes.add(el.asset);
  });
  const fields = scene.fields.filter((f) => f.bindings.length > 0 && f.bindings.every((b) => inside.has(b.element)));
  for (const f of fields) if (f.type === 'image-choice') f.choices.forEach((c) => hashes.add(c));
  return {
    format: CLIPBOARD_FORMAT,
    version: 1,
    elements: structuredClone(roots),
    assets: scene.assets.filter((a) => hashes.has(a.hash)),
    fields: structuredClone(fields),
  };
}

export function serializeClipboard(p: ClipboardPayload): string {
  return JSON.stringify(p);
}

/** Parse clipboard text; undefined when it is not (valid) PRISM Graphics content. */
export function parseClipboard(text: string): ClipboardPayload | undefined {
  if (!text.includes(CLIPBOARD_FORMAT)) return undefined;
  try {
    const r = PayloadSchema.safeParse(JSON.parse(text));
    return r.success ? (r.data as ClipboardPayload) : undefined;
  } catch {
    return undefined;
  }
}

/** Paste: fresh element and field ids, assets added, offset when pasting onto the same spot. Returns new ids. */
export function paste(scene: Scene, p: ClipboardPayload, at: InsertAt, offset = 0): { scene: Scene; ids: string[] } {
  let s = scene;
  for (const a of p.assets) s = ensureAsset(s, a);
  // ensureAsset may rename files, never hashes, so references stay valid.
  const { elements, ids } = cloneWithNewIds(s, p.elements);
  const placed = elements.map((el) => (offset ? ({ ...el, transform: { ...el.transform, x: el.transform.x + offset, y: el.transform.y + offset } } as Element) : el));
  s = addElements(s, placed, at);
  const taken = new Set(s.fields.map((f) => f.id));
  const fields: Field[] = [];
  for (const f of p.fields) {
    const id = taken.has(f.id) ? fieldIdFrom(f.title, taken) : f.id;
    taken.add(id);
    fields.push({ ...f, id, bindings: f.bindings.map((b) => ({ ...b, element: ids.get(b.element) ?? b.element })) } as Field);
  }
  if (fields.length) s = { ...s, fields: [...s.fields, ...fields] };
  return { scene: s, ids: placed.map((e) => e.id) };
}
